import React from 'react';
import { Outlet } from 'react-router-dom';

import AsideMenu from '../../features/menu/AsideMenu';
import HeaderIndex from '../header/HeaderIndex';
import { EmulationWatermark } from '../common/EmulationWatermark/EmulationWatermark';
import Breadcrumb from './Breadcrumb';
import BottomTabBar from './BottomTabBar';
import { useBreadcrumbs } from '../../store/BreadcrumbContext';

/*
  Fixed-shell layout — header and aside stay pinned to the viewport while only
  the main content area scrolls. Two tricks:
    1. Lock the outer flex row to exactly `100vh` and hide overflow so the
       browser window itself never scrolls.
    2. Give the inner column `min-h-0` so its child's `overflow-y: auto` can
       kick in (a flex item defaults to `min-height: auto`, which would
       otherwise force the column to grow to its content size).
  The fixed side-by-side shell holds at every viewport size — Vue ERP's
  `.aside-bar` keeps the rail a fixed vertical left sidebar on mobile too
  (it never stacks the aside on top), so we mirror that and let only the
  content area scroll internally.
*/
const APP_LAYOUT_CLASS =
  'flex h-screen overflow-hidden bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]';

const APP_LAYOUT_MAIN_CLASS =
  'flex-1 flex flex-col min-w-0 min-h-0';

// Mobile left inset is 1rem (16px) per QA 2026-08-21 — a hair inside
// the card borders, splitting the difference toward the KPI text
// column. Right stays 12px to keep the Export action near the edge.
const APP_LAYOUT_BREADCRUMB_CLASS =
  'flex items-center justify-between py-[6px] px-6 bg-[var(--color-bg-primary)] flex-none' +
  ' max-[768px]:pl-4 max-[768px]:pr-3';

const APP_LAYOUT_BREADCRUMB_ACTIONS_CLASS =
  'flex items-center gap-2';

// `flex flex-col` lets a page root opt into filling the content area
// (e.g. the AR Contacts boot loader uses `flex-1 min-h-0` + `min-h-full`
// to cover the area without overflowing it). Pages that don't set
// `flex-1` on their root keep their natural top-aligned stacking.
// `max-[768px]:pb-[88px]` reserves room for the fixed mobile
// `BottomTabBar` (~56px tab row + label + iOS safe-area inset) so the
// last list item stays above the bar while the content area scrolls.
// `overflow-x-hidden` is load-bearing: `overflow-y: auto` makes the
// unset x-axis compute to auto too, so ONE child slipping past its own
// containment turns the whole content area into a horizontal scroller —
// a swipe then strands the page half-shifted (observed on the mobile
// Orders screen). Wide content must scroll inside its own container.
const APP_LAYOUT_CONTENT_CLASS =
  'p-6 flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden ' +
  'max-[768px]:p-[0.1rem] max-[768px]:pb-[88px]';

const AppLayout: React.FC = () => {
  const { breadcrumbActions } = useBreadcrumbs();

  // The aside is a static sidebar at >=769px and simply hidden at
  // <=768px — mobile navigation lives in the BottomTabBar (+ its
  // slide-up MobileMenuSheet), so there is no hamburger-opened drawer.
  return (
    <div className={APP_LAYOUT_CLASS}>
      <AsideMenu />
      <div className={APP_LAYOUT_MAIN_CLASS}>
        <HeaderIndex />
        <div className={APP_LAYOUT_BREADCRUMB_CLASS}>
          <Breadcrumb />
          {breadcrumbActions && (
            <div className={APP_LAYOUT_BREADCRUMB_ACTIONS_CLASS}>{breadcrumbActions}</div>
          )}
        </div>
        <main className={APP_LAYOUT_CONTENT_CLASS}>
          <Outlet />
        </main>
      </div>
      {/*
        Fullscreen tiled "Emulation mode" watermark. Vue parity with
        `UserEmulation.vue:13-24` (`<n-watermark fullscreen>`). The
        component returns null when `isEmulate !== 'true'`, so this is
        a zero-cost render when the user isn't emulating. Mounted as a
        layout sibling (not inside the content area) so the watermark
        survives route changes without unmounting.
      */}
      <EmulationWatermark />
      {/* Mobile-only fixed bottom tab nav — Dashboard / Orders / Products /
          Purchasing / Inventory. Hidden by its own internal `md:hidden`
          so desktop is unaffected. */}
      <BottomTabBar />
    </div>
  );
};

export default AppLayout;
